import { Plus, X } from 'lucide-react';
import { Select } from '@/components/Select';
import { Input } from '@/components/Input';
import { WorkflowCondition, WorkflowConditionTree } from '@/services/workflows.service';
import { FieldOption, OPS_BY_KIND } from './workflowMeta';

export interface WorkflowLookups {
  team: Array<{ userUid: string; label: string }>;
  stages: Array<{ stageUid: string; label: string }>;
  pipelines: Array<{ pipelineUid: string; name: string }>;
}

interface Props {
  value: WorkflowConditionTree;
  onChange: (next: WorkflowConditionTree) => void;
  fields: FieldOption[];
  lookups: WorkflowLookups;
  emptyText: string;
}

export function ConditionEditor({ value, onChange, fields, lookups, emptyText }: Props) {
  const mode: 'all' | 'any' = value?.any?.length ? 'any' : 'all';
  const rows = (mode === 'any' ? value.any : value?.all) ?? [];
  const commit = (next: WorkflowCondition[], nextMode = mode) => onChange(next.length ? { [nextMode]: next } : {});
  const fieldFor = (path: string): FieldOption => fields.find(f => f.value === path) ?? { value: path, label: path, kind: 'text' };
  const update = (index: number, patch: Partial<WorkflowCondition>) => commit(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const add = () => {
    const first = fields[0];
    commit([...rows, { field: first.value, op: OPS_BY_KIND[first.kind][0].value, value: first.options?.[0]?.value ?? '' }]);
  };

  const valueControl = (row: WorkflowCondition, index: number) => {
    if (row.op === 'exists' || row.op === 'notExists') return null;
    const field = fieldFor(row.field);
    const current = row.value === undefined || row.value === null ? '' : String(row.value);
    const set = (next: string) => update(index, { value: field.kind === 'number' ? (next === '' ? '' : Number(next)) : next });
    const options = field.kind === 'enum' ? (field.options ?? []).map(o => ({ value: o.value, label: o.label }))
      : field.kind === 'user' ? lookups.team.map(u => ({ value: u.userUid, label: u.label }))
      : field.kind === 'stage' ? lookups.stages.map(s => ({ value: s.stageUid, label: s.label }))
      : field.kind === 'pipeline' ? lookups.pipelines.map(p => ({ value: p.pipelineUid, label: p.name }))
      : null;
    if (options) {
      return (
        <Select value={current} onChange={e => set(e.target.value)} className="!h-9 !text-xs">
          <option value="">Choose…</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      );
    }
    return <Input type={field.kind === 'number' ? 'number' : 'text'} value={current} onChange={e => set(e.target.value)} placeholder="Value" className="!h-9 !text-xs" />;
  };

  return (
    <div className="space-y-2">
      {rows.length === 0 && <p className="text-2xs text-text-muted">{emptyText}</p>}
      {rows.length > 1 && (
        <div className="flex items-center gap-2 text-2xs text-text-muted">
          Match
          <Select value={mode} onChange={e => commit(rows, e.target.value as 'all' | 'any')} className="!h-7 !w-auto !py-0 !text-2xs">
            <option value="all">all conditions</option>
            <option value="any">any condition</option>
          </Select>
        </div>
      )}
      {rows.map((row, index) => {
        const field = fieldFor(row.field);
        return (
          <div key={index} className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-surface p-2 sm:grid-cols-[1.4fr_1fr_1.2fr_auto]">
            <Select
              value={row.field}
              onChange={e => {
                const next = fieldFor(e.target.value);
                update(index, { field: next.value, op: OPS_BY_KIND[next.kind][0].value, value: next.options?.[0]?.value ?? '' });
              }}
              className="!h-9 !text-xs"
            >
              {!fields.some(f => f.value === row.field) && <option value={row.field}>{row.field}</option>}
              {fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
            <Select value={row.op} onChange={e => update(index, { op: e.target.value as WorkflowCondition['op'] })} className="!h-9 !text-xs">
              {OPS_BY_KIND[field.kind].map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              {!OPS_BY_KIND[field.kind].some(op => op.value === row.op) && <option value={row.op}>{row.op}</option>}
            </Select>
            <div className="min-w-0">{valueControl(row, index)}</div>
            <button type="button" onClick={() => commit(rows.filter((_, i) => i !== index))}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger" aria-label="Remove condition">
              <X size={13} />
            </button>
          </div>
        );
      })}
      <button type="button" onClick={add} className="inline-flex items-center gap-1 text-2xs font-medium text-primary hover:underline">
        <Plus size={12} /> Add condition
      </button>
    </div>
  );
}
